import React, { useState, useCallback, createContext, useContext, useEffect } from 'react';
import CustomAlert from './CustomAlert';
import { setAlertInstance } from './alertService';

const CustomAlertContext = createContext();

export const useCustomAlert = () => {
    const context = useContext(CustomAlertContext);
    if (!context) {
        throw new Error('useCustomAlert must be used within a CustomAlertProvider');
    }
    return context;
};

export const CustomAlertProvider = ({ children }) => {
    const [alertConfig, setAlertConfig] = useState({
        isOpen: false,
        type: 'info',
        title: '',
        message: '',
        confirmText: 'OK',
        cancelText: 'Cancel',
        showCancel: false,
        onConfirm: null,
        onCancel: null
    });

    const showAlert = useCallback(({
        type = 'info',
        title = '',
        message = '',
        confirmText = 'OK',
        cancelText = 'Cancel',
        showCancel = false,
        onConfirm = null,
        onCancel = null
    }) => {
        return new Promise((resolve) => {
            setAlertConfig({
                isOpen: true,
                type,
                title,
                message,
                confirmText,
                cancelText,
                showCancel,
                onConfirm: () => {
                    if (onConfirm) onConfirm();
                    resolve(true);
                },
                onCancel: () => {
                    if (onCancel) onCancel();
                    resolve(false);
                }
            });
        });
    }, []);

    // Convenience methods
    const showSuccess = useCallback((message, title = 'Success') => {
        return showAlert({ type: 'success', title, message });
    }, [showAlert]);

    const showError = useCallback((message, title = 'Error') => {
        return showAlert({ type: 'error', title, message });
    }, [showAlert]);

    const showWarning = useCallback((message, title = 'Warning') => {
        return showAlert({ type: 'warning', title, message });
    }, [showAlert]);

    const showInfo = useCallback((message, title = 'Information') => {
        return showAlert({ type: 'info', title, message });
    }, [showAlert]);

    const showConfirm = useCallback((message, title = 'Confirm', options = {}) => {
        return showAlert({
            type: 'warning',
            title,
            message,
            showCancel: true,
            confirmText: options.confirmText || 'Confirm',
            cancelText: options.cancelText || 'Cancel',
            onConfirm: options.onConfirm,
            onCancel: options.onCancel
        });
    }, [showAlert]);

    const closeAlert = useCallback(() => {
        setAlertConfig(prev => ({ ...prev, isOpen: false }));
    }, []);

    const contextValue = {
        showAlert,
        showSuccess,
        showError,
        showWarning,
        showInfo,
        showConfirm,
        closeAlert
    };

    // Register this instance globally so it can be used by alert service
    useEffect(() => {
        setAlertInstance(contextValue);
        
        // Override native alert
        const originalAlert = window.alert;
        window.alert = (message) => {
            showInfo(message, 'Alert');
        };
        
        return () => {
            window.alert = originalAlert;
        };
    }, [contextValue]);

    return (
        <CustomAlertContext.Provider value={contextValue}>
            {children}
            <CustomAlert
                isOpen={alertConfig.isOpen}
                type={alertConfig.type}
                title={alertConfig.title}
                message={alertConfig.message}
                confirmText={alertConfig.confirmText}
                cancelText={alertConfig.cancelText}
                showCancel={alertConfig.showCancel}
                onConfirm={alertConfig.onConfirm}
                onClose={() => {
                    if (alertConfig.onCancel) {
                        alertConfig.onCancel();
                    }
                    closeAlert();
                }}
            />
        </CustomAlertContext.Provider>
    );
};

export default useCustomAlert;